import {damsFetch} from "../app/damsFetch";
import {clientLog} from "../clientLog";
import decamelizeKeysDeep from "decamelize-keys-deep";

const POLL_INTERVAL_MS = 2000;
const MAX_WAIT_MS = 15 * 60 * 1000;
const FINISHED = ['done', 'failed'];
const NO_RESULT = {status: 'failed', error: 'no result returned for model'};
const OUTCOME_UNKNOWN = {status: 'unknown', error: 'outcome unknown: publishing is still running'};
const NOT_FOUND = 404;

const wait = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Publishes models to the NB-API through a background job (DE-1698). The batch is submitted to
 * '/nb/publish/jobs', which answers at once; the backend then publishes the models one at a time
 * and untags any that fail. This polls the job until it has finished, so no request has to stay
 * open while NB is being called.
 *
 * Against a backend without the job endpoint (404), it falls back to the synchronous '/nb/publish'.
 *
 * @param {Array<Object>} models - The models to publish, each must contain at least 'id' and 'uniqueId'.
 * @param {Object} [options] - Polling settings; the defaults are for production.
 * @param {number} [options.pollIntervalMs] - Time between status requests.
 * @param {number} [options.maxWaitMs] - How long to poll before reporting the unfinished models as unknown.
 * @param {function(number): Promise} [options.sleep] - Waits the given number of milliseconds.
 * @param {function({completed: number, total: number})} [options.onProgress] - Told how far the job has got.
 * @returns {Promise<Array<{model: Object, result: Object}>>} Resolves with one entry per model,
 *          pairing the original model with its result. `result.status` is 'published' for a
 *          model NB accepted, 'unknown' for one still unresolved when the wait ran out, and
 *          anything else for a failure; never rejects.
 */
export const publishToNbBatch = async (models, {
    pollIntervalMs = POLL_INTERVAL_MS,
    maxWaitMs = MAX_WAIT_MS,
    sleep = wait,
    onProgress = () => {},
} = {}) => {
    const validModels = (models || []).filter(model => model && model.id);

    if (validModels.length === 0) {
        clientLog('warning', 'No models provided for batch publishing to NB.', 'publishtonb');
        return [];
    }

    const body = JSON.stringify(decamelizeKeysDeep({modelIds: validModels.map(model => model.uniqueId)}));

    let jobId;
    try {
        ({jobId} = await damsFetch('/nb/publish/jobs', {method: 'POST', body}));
    } catch (error) {
        if (error === NOT_FOUND) {
            return publishSynchronously(validModels, body);
        }
        return failAll(validModels);
    }

    onProgress({completed: 0, total: validModels.length});

    const maxPolls = Math.ceil(maxWaitMs / pollIntervalMs);
    let job = null;
    for (let poll = 0; poll < maxPolls; poll++) {
        await sleep(pollIntervalMs);
        try {
            job = await damsFetch(`/nb/publish/jobs/${jobId}`);
        } catch (error) {
            continue;
        }
        onProgress({completed: job.completed, total: job.total});
        if (FINISHED.includes(job.status)) {
            return pairWithResults(validModels, job.results, NO_RESULT);
        }
    }

    clientLog('error', `gave up waiting for NB publish job ${jobId}`, 'publishtonb');
    return pairWithResults(validModels, job?.results || [], OUTCOME_UNKNOWN);
};

/**
 * The synchronous '/nb/publish' used before DE-1698, kept for a backend without the job endpoint.
 */
const publishSynchronously = async (validModels, body) => {
    try {
        const response = await damsFetch('/nb/publish', {method: 'POST', body});
        return pairWithResults(validModels, response?.results || [], NO_RESULT);
    } catch (error) {
        return failAll(validModels);
    }
};

/**
 * Pairs each model with its result, logging the outcome; a model without a result gets missingResult.
 */
const pairWithResults = (validModels, results, missingResult) => {
    const resultByModelId = new Map(results.map(result => [result.modelId, result]));

    return validModels.map(model => {
        const result = resultByModelId.get(model.uniqueId) || {...missingResult};

        if (result.status === 'published') {
            clientLog('info', `successfully submitted model ${model.uniqueId} to NB-API`, 'publishtonb');
        } else if (result.status === OUTCOME_UNKNOWN.status) {
            clientLog('warning', `outcome of submitting model ${model.uniqueId} to NB-API is unknown`, 'publishtonb');
        } else {
            clientLog('error', `failed to submit model ${model.uniqueId} to NB-API`, 'publishtonb');
        }

        return {model, result};
    });
};

const failAll = (validModels) => {
    clientLog('error', `error submitting batch of ${validModels.length} models to NB-API.`, 'publishtonb');
    return validModels.map(model => ({
        model,
        result: {status: 'failed', error: 'error submitting to NB-API'}
    }));
};
